import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import StatusBadge from '../../components/StatusBadge';
import API from '../../utils/api';
import { formatDistanceToNow } from 'date-fns';
import { id } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { Clock, RotateCcw, User, CheckCircle, RefreshCw } from 'lucide-react';
import ArticleStats from '../../components/ArticleStats';
import './ProducerDashboard.css';

export default function ProducerDashboard() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({ articles: [], totalViews: 0, avgViews: 0 });

  useEffect(() => {
    fetchArticles();
    API.get('/articles/producer/stats').then(r => setStats({ articles: r.data.articles || [], totalViews: r.data.totalViews || 0, avgViews: r.data.avgViews || 0 })).catch(() => {});
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const res = await API.get('/articles/producer/queue');
      setArticles(res.data.articles || []);
    } catch { toast.error('Gagal memuat antrian artikel'); }
    finally { setLoading(false); }
  };

  const waiting   = articles.filter(a => a.status === 'review_producer');
  const returned  = articles.filter(a => a.status === 'revision_editor');
  const atReader  = articles.filter(a => ['review_reader','revision_reader'].includes(a.status));
  const done      = articles.filter(a => ['approved','published'].includes(a.status));

  const filtered = filter === 'all' ? articles : filter === 'waiting' ? waiting : filter === 'returned' ? returned : filter === 'reader' ? atReader : done;

  return (
    <DashboardLayout title="Dashboard Producer" subtitle="Pantau dan review artikel dari tim editor">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <button className="btn btn-ghost btn-sm" onClick={fetchArticles}><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="stats-row">
        {[
          { label: 'Menunggu Review', val: waiting.length, icon: <Clock size={20} />, color: 'var(--ki-text)', bg: 'rgba(212,105,10,0.10)' },
          { label: 'Dikembalikan ke Editor', val: returned.length, icon: <RotateCcw size={20} />, color: 'var(--ki-text)', bg: 'rgba(0,21,64,0.08)' },
          { label: 'Di Reader', val: atReader.length, icon: <User size={20} />, color: 'var(--ki-text)', bg: 'rgba(0,21,64,0.08)' },
          { label: 'Selesai / Publish', val: done.length, icon: <CheckCircle size={20} />, color: 'var(--ki-text)', bg: 'rgba(212,105,10,0.10)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-card-glow" style={{ background: s.bg }} />
            <div style={{ color: s.color }}>{s.icon}</div>
            <div className="stat-val">{s.val}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="ki-tabs" style={{ marginBottom: 20 }}>
        {[
          { key: 'all', label: `Semua (${articles.length})` },
          { key: 'waiting', label: `Menunggu Review (${waiting.length})` },
          { key: 'returned', label: `Dikembalikan (${returned.length})` },
          { key: 'reader', label: `Di Reader (${atReader.length})` },
          { key: 'done', label: `Selesai (${done.length})` },
        ].map(t => (
          <button key={t.key} className={`ki-tab ${filter === t.key ? 'active' : ''}`} onClick={() => setFilter(t.key)}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="ki-card">{[...Array(4)].map((_, i) => <div key={i} className="shimmer" style={{ height: 52, borderRadius: 8, marginBottom: 10 }} />)}</div>
      ) : filtered.length === 0 ? (
        <div className="ki-card" style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Tidak ada artikel di sini</div>
          <p style={{ color: 'var(--ki-text-muted)', fontSize: 14 }}>Tidak ada artikel yang sesuai filter saat ini.</p>
        </div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead>
              <tr><th>Judul</th><th>Kategori</th><th>Editor</th><th>Status</th><th>Terakhir Update</th><th>Aksi</th></tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a._id} style={{ background: a.status === 'review_producer' ? 'rgba(245,158,11,0.03)' : '' }}>
                  <td style={{ fontWeight: 600, fontSize: 14, maxWidth: 260 }}>{a.title || a.what}</td>
                  <td><span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: 'rgba(0,21,64,0.10)', color: 'var(--in-blue)', fontWeight: 600 }}>{a.category?.name || '-'}</span></td>
                  <td style={{ fontSize: 13, color: 'var(--ki-text-muted)' }}>{a.assignedEditor?.name || '-'}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td style={{ fontSize: 12, color: 'var(--ki-text-faint)' }}>{formatDistanceToNow(new Date(a.updatedAt), { addSuffix: true, locale: id })}</td>
                  <td><Link to={`/producer/artikel/${a._id}`} className="btn btn-secondary btn-sm">Review</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
      <ArticleStats
        articles={stats.articles}
        totalViews={stats.totalViews}
        avgViews={stats.avgViews}
        label="Statistik Artikel yang Saya Review"
        secondaryCol="assignedEditor"
        secondaryLabel="Editor"
      />
    </DashboardLayout>
  );
}
